// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CautionsList from "../../shared/CautionsList";
import AxisSummary from "../../shared/AxisSummary";
import SummaryCard, { NothingToSummarise } from "../../shared/SummaryCard";
import { FigureRow, Figures } from "../../shared/FigureRow";
import DistanceRow from "../../shared/DistanceRow";

// Constants
import { NEAR_TEST_DISTANCE_CM } from "src/constants/enums";

// Types
import {
  ExamSectionReportType,
  ObjectiveAlignmentSummaryType,
  SubjectiveAngleSummaryType,
} from "src/services/exams/types";

// Utils
import { num } from "../../shared/formatters";

type SummaryType = NonNullable<
  ExamSectionReportType["postProcessing"]["summary"]
>;
type CyclodeviationSummaryType = SummaryType["Cyclodeviation"];

type SummarySectionType = {
  summary: SummaryType | null | undefined;
};

const MODULE_LABELS: Record<string, string> = {
  ObjectiveAlignment: "Objective Alignment",
  SubjectiveAngle: "Subjective Angle",
  Cyclodeviation: "Cyclodeviation",
};

const ObjectiveAlignmentFigures = ({
  data,
}: {
  data: ObjectiveAlignmentSummaryType;
}) => {
  const { t } = useTranslation();
  return (
    <Figures maxColumns={2}>
      <FigureRow
        label={t("Horizontal")}
        value={
          <AxisSummary
            axis={data.horizontal}
            pattern={data.horizontal.pattern}
          />
        }
      />
      <FigureRow
        label={t("Vertical")}
        value={
          <AxisSummary axis={data.vertical} pattern={data.vertical.pattern} />
        }
      />
      {data.leftFixing && (
        <>
          <FigureRow
            label={t("Left Fixing (Horizontal)")}
            value={<AxisSummary axis={data.leftFixing.horizontal} />}
          />
          <FigureRow
            label={t("Left Fixing (Vertical)")}
            value={<AxisSummary axis={data.leftFixing.vertical} />}
          />
        </>
      )}
      {data.rightFixing && (
        <>
          <FigureRow
            label={t("Right Fixing (Horizontal)")}
            value={<AxisSummary axis={data.rightFixing.horizontal} />}
          />
          <FigureRow
            label={t("Right Fixing (Vertical)")}
            value={<AxisSummary axis={data.rightFixing.vertical} />}
          />
        </>
      )}
      <FigureRow
        label={t("Laterality")}
        value={
          <Typography sx={{ fontSize: "12px" }}>
            {data.laterality && data.laterality !== "None"
              ? t(data.laterality)
              : t("N/A")}
          </Typography>
        }
      />
    </Figures>
  );
};

// One card per distance, each with its own cautions. A distance with nothing to report (the
// summary is null for a run that didn't complete) still gets its card, so it's clear it's missing.
const ObjectiveAlignmentCard = ({
  distanceLabel,
  data,
}: {
  distanceLabel: string;
  data: ObjectiveAlignmentSummaryType | null | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <SummaryCard
      title={`${t(MODULE_LABELS.ObjectiveAlignment)} - ${distanceLabel}`}
      cautions={data?.cautions ?? null}
    >
      {!data ? (
        <NothingToSummarise />
      ) : (
        <ObjectiveAlignmentFigures data={data} />
      )}
    </SummaryCard>
  );
};

const SubjectiveAngleFigures = ({
  data,
}: {
  data: SubjectiveAngleSummaryType;
}) => {
  const { t } = useTranslation();
  return (
    <Figures maxColumns={2}>
      <FigureRow
        label={t("Horizontal")}
        value={<AxisSummary axis={data.horizontal} />}
      />
      <FigureRow
        label={t("Vertical")}
        value={<AxisSummary axis={data.vertical} />}
      />
      <FigureRow
        label={t("Correspondence")}
        value={
          <Typography sx={{ fontSize: "12px" }}>
            {data.correspondence ? t(data.correspondence) : t("N/A")}
          </Typography>
        }
      />
      <FigureRow
        label={t("Angle of Anomaly")}
        value={num(data.angleOfAnomalyPrismDioptres, 1, "Δ")}
      />
      <FigureRow
        label={t("Perception Floor")}
        value={
          <Typography sx={{ fontSize: "12px" }}>
            {data.perceptionFloor ? t(data.perceptionFloor) : t("N/A")}
          </Typography>
        }
      />
      <FigureRow
        label={t("Suppressing Eye")}
        value={
          <Typography sx={{ fontSize: "12px" }}>
            {data.suppressingEye ? t(data.suppressingEye) : t("N/A")}
          </Typography>
        }
      />
    </Figures>
  );
};

// One card per distance, as with objective alignment.
const SubjectiveAngleCard = ({
  distanceLabel,
  data,
}: {
  distanceLabel: string;
  data: SubjectiveAngleSummaryType | null | undefined;
}) => {
  const { t } = useTranslation();
  return (
    <SummaryCard
      title={`${t(MODULE_LABELS.SubjectiveAngle)} - ${distanceLabel}`}
      cautions={data?.cautions ?? null}
    >
      {!data ? (
        <Box mb="8px">
          <NothingToSummarise />
        </Box>
      ) : (
        <SubjectiveAngleFigures data={data} />
      )}
    </SummaryCard>
  );
};

const CycloValue = ({
  value,
}: {
  value: { degrees: number | null; direction: string | null };
}) => {
  const { t } = useTranslation();

  return (
    <Box component="span" display="inline-flex" alignItems="center" gap="6px">
      <Box component="span">{num(value?.degrees, 1, "°")}</Box>
      {" | "}
      <Typography sx={{ fontSize: "12px" }}>
        {value?.direction && value?.direction !== "None"
          ? t(value?.direction)
          : t("N/A")}
      </Typography>
    </Box>
  );
};

const CyclodeviationCard = ({ data }: { data: CyclodeviationSummaryType }) => {
  const { t } = useTranslation();
  return (
    <SummaryCard
      title={t(MODULE_LABELS.Cyclodeviation)}
      cautions={data?.cautions ?? null}
    >
      {!data ? (
        <Box mb="8px">
          <NothingToSummarise />
        </Box>
      ) : (
        <Figures>
          <FigureRow
            label={t("Total (Binocular)")}
            value={<CycloValue value={data.total} />}
          />
          <FigureRow
            label={t("Left Eye")}
            value={<CycloValue value={data.leftEye} />}
          />
          <FigureRow
            label={t("Right Eye")}
            value={<CycloValue value={data.rightEye} />}
          />
        </Figures>
      )}
    </SummaryCard>
  );
};

// The technician view: the few headline numbers read off each module, built straight from
// postProcessing.summary rather than the verbose per-window modules block.
const SummarySection = ({ summary }: SummarySectionType) => {
  const { t } = useTranslation();

  if (!summary) return null;

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap="16px" sx={{ mb: "8px" }}>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Section Cautions")}
          </Typography>
          <CautionsList cautions={summary.cautions} />
        </Box>
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Incomplete Modules")}
          </Typography>
          {!summary.incompleteModules ||
          summary.incompleteModules.length === 0 ? (
            <Typography fontSize="12px">{t("N/A")}</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap="4px">
              {summary.incompleteModules.map((key) => (
                <Typography key={key} sx={{ fontSize: "12px" }}>
                  {t(MODULE_LABELS[key] ?? key)}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        <DistanceRow>
          <ObjectiveAlignmentCard
            distanceLabel={t("Far")}
            data={summary.ObjectiveAlignment?.far}
          />
          <ObjectiveAlignmentCard
            distanceLabel={t("Near ({{distance}} cm)", {
              distance: NEAR_TEST_DISTANCE_CM,
            })}
            data={summary.ObjectiveAlignment?.near}
          />
        </DistanceRow>
        <DistanceRow>
          <SubjectiveAngleCard
            distanceLabel={t("Far")}
            data={summary.SubjectiveAngle?.far}
          />
          <SubjectiveAngleCard
            distanceLabel={t("Near ({{distance}} cm)", {
              distance: NEAR_TEST_DISTANCE_CM,
            })}
            data={summary.SubjectiveAngle?.near}
          />
        </DistanceRow>
        <CyclodeviationCard data={summary.Cyclodeviation} />
      </Box>
    </Box>
  );
};

export default SummarySection;
